import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  BOARD_ASPECT, BOARD_COLORS, COLORS, WIDTHS, MAX_TEXT, letterbox, normalise, inside,
  eraserHits, renderStrokes
} from './drawCore';
import { tokenColor, cssVar } from './drawChannel';
import './draw.css';

/**
 * The whiteboard: a shared sheet of paper in the middle of the call.
 *
 * Gordon, 22 October: "users can interact with each other over a call using
 * the whiteboard." So it is a stage tile like anybody's video, opened from
 * the footer; the first person to open it opens it for everybody, and anybody
 * can hide it again for themselves without taking it away from the others.
 *
 * THE PAPER IS 16:9, ALWAYS. The tile is whatever shape the window makes it,
 * and the paper sits letterboxed inside it - so a stroke at a third of the
 * way across is a third of the way across on a phone and on a wide monitor
 * alike. Coordinates leave this file as fractions of the paper (drawCore's
 * normalise), never as pixels.
 *
 * POINTER EVENTS, so a mouse, a pen and a finger are the same code; pressure
 * is ignored, because a line that is thicker on one person's tablet than it
 * is for everybody else is a line drawn differently for each of them. The
 * canvas takes touch-action: none, or a finger drawing a line on a phone
 * scrolls the page instead.
 *
 * IT IS A SHARED BOARD, AS IN TEAMS: the eraser takes anybody's strokes, and
 * so does Clear all (after asking). Undo is your own last stroke only; Clear
 * mine is yours only.
 */

/* ------------------------------------------------------------------ icons */

const Icon = ({ d, fill = false }) => (
  <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true" focusable="false"
    fill={fill ? 'currentColor' : 'none'} stroke={fill ? 'none' : 'currentColor'}
    strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    {[].concat(d).map((p) => <path key={p} d={p} />)}
  </svg>
);

export const ICONS = {
  pen: <Icon d={['M4 20l4-1L19 8a2.1 2.1 0 0 0-3-3L5 16l-1 4z', 'M14 7l3 3']} />,
  highlighter: <Icon d={['M9 15l-3 3v2h4l3-3', 'M9 15l6-10 4 4-10 6', 'M3 21h18']} />,
  line: <Icon d="M5 19L19 5" />,
  arrow: <Icon d={['M5 19L19 5', 'M10 5h9v9']} />,
  rect: <Icon d="M4 6h16v12H4z" />,
  ellipse: <Icon d="M12 5c4.4 0 8 3.1 8 7s-3.6 7-8 7-8-3.1-8-7 3.6-7 8-7z" />,
  text: <Icon d={['M5 6V4h14v2', 'M12 4v16', 'M9 20h6']} />,
  eraser: <Icon d={['M16 3l5 5-11 11H5l-3-3L13 5z', 'M8 8l8 8', 'M10 19h11']} />,
  laser: <Icon d={['M12 12m-2.5 0a2.5 2.5 0 1 0 5 0a2.5 2.5 0 1 0 -5 0', 'M12 3v3', 'M12 18v3', 'M3 12h3', 'M18 12h3']} />,
  undo: <Icon d={['M9 14L4 9l5-5', 'M4 9h10a6 6 0 0 1 0 12h-3']} />,
  clear: <Icon d={['M4 7h16', 'M10 11v6', 'M14 11v6', 'M6 7l1 13h10l1-13', 'M9 7V4h6v3']} />,
  save: <Icon d={['M12 4v11', 'M7 10l5 5 5-5', 'M5 20h14']} />,
  hide: <Icon d={['M6 6l12 12', 'M18 6L6 18']} />
};

export const TOOL_NAMES = {
  pen: 'Pen', highlighter: 'Highlighter', line: 'Line', arrow: 'Arrow', rect: 'Rectangle',
  ellipse: 'Ellipse', text: 'Text', eraser: 'Eraser', laser: 'Laser pointer'
};

export const COLOR_NAMES = {
  ink: 'Ink', red: 'Red', orange: 'Orange', yellow: 'Yellow', green: 'Green',
  blue: 'Blue', purple: 'Purple', pink: 'Pink'
};

/* ------------------------------------------------------------- painting */

/** A colour name to the colour this page's theme gives it. */
export const colorOf = (name) => tokenColor(name, COLORS);

/**
 * Everything on one surface: the strokes, then whatever is fading - the
 * laser's trail and the name tags of whoever is drawing. Shared by the board
 * and every annotation layer, so a mark looks the same wherever it is.
 */
export function paintSurface(ctx, engine, target, box) {
  renderStrokes(ctx, engine.strokes(target), box, colorOf);
  const { lasers, cursors } = engine.ephemera(target);
  const laserColor = colorOf('red') || '#dc3a30';
  for (const l of lasers) {
    const pts = l.points.map((p) => [box.x + p.x * box.w, box.y + p.y * box.h, p.age]);
    ctx.save();
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.strokeStyle = laserColor;
    for (let i = 1; i < pts.length; i += 1) {
      ctx.globalAlpha = Math.max(0, 0.55 * (1 - pts[i][2]));
      ctx.lineWidth = 2 + 4 * (1 - pts[i][2]);
      ctx.beginPath();
      ctx.moveTo(pts[i - 1][0], pts[i - 1][1]);
      ctx.lineTo(pts[i][0], pts[i][1]);
      ctx.stroke();
    }
    const head = pts[pts.length - 1];
    if (head) {
      ctx.globalAlpha = Math.max(0, 1 - head[2]);
      ctx.fillStyle = laserColor;
      ctx.shadowColor = laserColor;
      ctx.shadowBlur = 12;
      ctx.beginPath();
      ctx.arc(head[0], head[1], 6, 0, Math.PI * 2);
      ctx.fill();
    }
    ctx.restore();
  }
  for (const c of cursors) {
    const x = box.x + c.x * box.w;
    const y = box.y + c.y * box.h;
    const name = String(c.name || 'Somebody').slice(0, 24);
    ctx.save();
    ctx.globalAlpha = Math.max(0, 1 - c.age * c.age);
    ctx.font = '600 12px system-ui, -apple-system, "Segoe UI", sans-serif';
    const w = ctx.measureText(name).width + 14;
    // 'ink' is the page's text colour: as a tag background it would be white
    // in dark mode under white text, so the tag takes the accent instead.
    ctx.fillStyle = (c.color !== 'ink' && colorOf(c.color)) || colorOf('blue') || '#1f6fe0';
    const tx = Math.min(x + 10, box.x + box.w - w);
    const ty = Math.max(box.y, y - 26);
    ctx.beginPath();
    if (ctx.roundRect) ctx.roundRect(tx, ty, w, 20, 10); else ctx.rect(tx, ty, w, 20);
    ctx.fill();
    ctx.fillStyle = '#fff';
    ctx.textBaseline = 'middle';
    ctx.fillText(name, tx + 7, ty + 10);
    ctx.restore();
  }
}

/** A canvas as a PNG download, without a server. */
export function downloadCanvas(canvas, filename) {
  return new Promise((resolve) => {
    const done = (blob) => {
      if (!blob) { resolve(false); return; }
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 4000);
      resolve(true);
    };
    try { canvas.toBlob(done, 'image/png'); } catch { resolve(false); }
  });
}

export const stamp = () => {
  const d = new Date();
  const p = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}-${p(d.getHours())}${p(d.getMinutes())}`;
};

/**
 * Keep a canvas the size of its box, at the screen's pixel density, and
 * repaint it whenever the box or the drawing changes. Returns the box (CSS
 * pixels) so pointer handlers can normalise against the same rectangle the
 * paint used.
 */
export function useCanvas(canvasRef, holderRef, engine, target, paint) {
  const sizeRef = useRef({ w: 0, h: 0, dpr: 1 });
  const paintRef = useRef(paint);
  paintRef.current = paint;
  const frame = useRef(null);

  const redraw = useCallback(() => {
    if (frame.current) return;
    frame.current = window.requestAnimationFrame(() => {
      frame.current = null;
      const canvas = canvasRef.current;
      if (!canvas) return;
      const { w, h, dpr } = sizeRef.current;
      const ctx = canvas.getContext('2d');
      if (!ctx) return;
      ctx.setTransform(1, 0, 0, 1, 0, 0);
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      paintRef.current(ctx, w, h);
    });
  }, [canvasRef]);

  useEffect(() => {
    const holder = holderRef.current;
    const canvas = canvasRef.current;
    if (!holder || !canvas) return undefined;
    const fit = () => {
      const r = holder.getBoundingClientRect();
      const dpr = Math.min(3, window.devicePixelRatio || 1);
      const w = Math.max(1, Math.round(r.width));
      const h = Math.max(1, Math.round(r.height));
      if (w === sizeRef.current.w && h === sizeRef.current.h && dpr === sizeRef.current.dpr) return;
      sizeRef.current = { w, h, dpr };
      canvas.width = Math.round(w * dpr);
      canvas.height = Math.round(h * dpr);
      redraw();
    };
    fit();
    const ro = typeof ResizeObserver === 'function' ? new ResizeObserver(fit) : null;
    ro?.observe(holder);
    window.addEventListener('resize', fit);
    const off = engine.subscribe(target, redraw);
    // The colours are the theme's tokens, read at paint time - so a change of
    // theme (the system's, or Jarvis's own switch) is a reason to repaint.
    const scheme = window.matchMedia ? window.matchMedia('(prefers-color-scheme: dark)') : null;
    scheme?.addEventListener?.('change', redraw);
    const themed = typeof MutationObserver === 'function' ? new MutationObserver(redraw) : null;
    themed?.observe(document.documentElement, { attributes: true, attributeFilter: ['data-theme', 'class'] });
    return () => {
      ro?.disconnect();
      window.removeEventListener('resize', fit);
      scheme?.removeEventListener?.('change', redraw);
      themed?.disconnect();
      off();
      if (frame.current) { window.cancelAnimationFrame(frame.current); frame.current = null; }
    };
  }, [holderRef, canvasRef, engine, target, redraw]);

  return { sizeRef, redraw };
}

/** Where a pointer is, in CSS pixels of the element it is over. */
const local = (e, el) => {
  const r = el.getBoundingClientRect();
  return [e.clientX - r.left, e.clientY - r.top];
};

/** Every position a fast pointer passed through, not just the last. */
const coalesced = (e) => {
  const list = typeof e.nativeEvent?.getCoalescedEvents === 'function' ? e.nativeEvent.getCoalescedEvents() : [];
  return list.length ? list : [e];
};

/* ------------------------------------------------------------- the board */

const BOARD_TOOLS = ['pen', 'highlighter', 'line', 'arrow', 'rect', 'ellipse', 'text', 'eraser'];
const boxOf = (w, h) => letterbox(w, h, BOARD_ASPECT);

const Whiteboard = ({ engine, onHide, people = 1 }) => {
  const [tool, setTool] = useState('pen');
  const [color, setColor] = useState('ink');
  const [clearing, setClearing] = useState(null); // null | 'menu' | 'confirm'
  const [textAt, setTextAt] = useState(null);
  const [textValue, setTextValue] = useState('');
  const [ring, setRing] = useState(null);
  const [saved, setSaved] = useState(false);
  const areaRef = useRef(null);
  const canvasRef = useRef(null);
  const strokeRef = useRef(null);
  const erasingRef = useRef(false);
  const textRef = useRef(null);

  const paint = useCallback((ctx, w, h) => {
    const box = boxOf(w, h);
    // The paper, on the tile's own background, with a hairline round it.
    ctx.fillStyle = cssVar('--color-bg-raised') || '#fff';
    ctx.fillRect(box.x, box.y, box.w, box.h);
    ctx.strokeStyle = cssVar('--color-border-dark') || 'rgba(0,0,0,0.16)';
    ctx.lineWidth = 1;
    ctx.strokeRect(box.x + 0.5, box.y + 0.5, box.w - 1, box.h - 1);
    // For the recording, which crops the canvas to the paper.
    const canvas = canvasRef.current;
    if (canvas) {
      const dpr = Math.min(3, window.devicePixelRatio || 1);
      canvas.dataset.box = [box.x, box.y, box.w, box.h].map((v) => Math.round(v * dpr)).join(',');
    }
    ctx.save();
    ctx.beginPath();
    ctx.rect(box.x, box.y, box.w, box.h);
    ctx.clip();
    paintSurface(ctx, engine, 'board', box);
    ctx.restore();
  }, [engine]);

  const { sizeRef } = useCanvas(canvasRef, areaRef, engine, 'board', paint);

  const box = () => boxOf(sizeRef.current.w, sizeRef.current.h);

  const eraseAt = (pt) => {
    const ids = eraserHits(engine.strokes('board'), pt, 0.022, BOARD_ASPECT);
    if (ids.length) engine.erase(ids);
  };

  const down = (e) => {
    if (e.button > 0 || !areaRef.current) return;
    const [x, y] = local(e, areaRef.current);
    const b = box();
    if (!inside(x, y, b)) return;
    const pt = normalise(x, y, b);
    if (tool === 'text') {
      e.preventDefault();
      setTextAt({ x, y, pt });
      setTextValue('');
      setTimeout(() => textRef.current?.focus(), 0);
      return;
    }
    try { e.currentTarget.setPointerCapture(e.pointerId); } catch { /* fine */ }
    if (tool === 'eraser') { erasingRef.current = true; eraseAt(pt); return; }
    strokeRef.current = engine.begin('board', { tool, color, width: WIDTHS[tool], point: pt });
  };

  const move = (e) => {
    if (!areaRef.current) return;
    if (tool === 'eraser') {
      const [x, y] = local(e, areaRef.current);
      setRing({ x, y });
    }
    if (!strokeRef.current && !erasingRef.current) return;
    for (const ev of coalesced(e)) {
      const [x, y] = local(ev, areaRef.current);
      const pt = normalise(x, y, box());
      if (strokeRef.current) engine.extend(strokeRef.current, pt);
      else eraseAt(pt);
    }
  };

  const up = () => {
    if (strokeRef.current) engine.end(strokeRef.current);
    strokeRef.current = null;
    erasingRef.current = false;
  };

  const commitText = () => {
    const t = textValue.trim();
    if (t && textAt) {
      const id = engine.begin('board', { tool: 'text', color, width: WIDTHS.text, point: textAt.pt, text: t.slice(0, MAX_TEXT) });
      engine.end(id);
    }
    setTextAt(null);
    setTextValue('');
  };

  /** The board as a picture: the paper and everything on it, at 1080p. */
  const exportPng = async () => {
    const c = document.createElement('canvas');
    c.width = 1920;
    c.height = 1080;
    const ctx = c.getContext('2d');
    ctx.fillStyle = cssVar('--color-bg-raised') || '#fff';
    ctx.fillRect(0, 0, c.width, c.height);
    renderStrokes(ctx, engine.strokes('board'), { x: 0, y: 0, w: c.width, h: c.height }, colorOf);
    if (await downloadCanvas(c, `whiteboard-${stamp()}.png`)) {
      setSaved(true);
      setTimeout(() => setSaved(false), 2200);
    }
  };

  // Ctrl/Cmd+Z takes back my last stroke while the board has focus.
  const keys = (e) => {
    if ((e.ctrlKey || e.metaKey) && !e.shiftKey && e.key.toLowerCase() === 'z' && e.target === e.currentTarget) {
      e.preventDefault();
      engine.undo('board');
    }
  };

  return (
    <section className="dr-board" aria-label="Whiteboard" onKeyDown={keys} tabIndex={-1}>
      <div className="dr-head">
        <div className="dr-head-text">
          <strong className="dr-title">Whiteboard</strong>
          <span className="dr-sub">
            {people > 1 ? `Everybody in the call can draw on it` : 'Anybody who joins sees it too'}
          </span>
        </div>
        <button type="button" className="dr-icon-btn dr-hide" onClick={onHide}
          aria-label="Hide the whiteboard for me" title="Hide the whiteboard (for you only)">
          {ICONS.hide}<span className="dr-btn-word">Hide</span>
        </button>
      </div>

      <div className="dr-tools" role="toolbar" aria-label="Whiteboard tools">
        <div className="dr-group" role="radiogroup" aria-label="Tool">
          {BOARD_TOOLS.map((t) => (
            <button key={t} type="button" className="dr-tool" role="radio" aria-checked={tool === t}
              data-tool={t} onClick={() => { setTool(t); setRing(null); }}
              aria-label={TOOL_NAMES[t]} title={TOOL_NAMES[t]}>
              {ICONS[t]}
            </button>
          ))}
        </div>
        <span className="dr-sep" aria-hidden="true" />
        <div className="dr-group" role="radiogroup" aria-label="Colour">
          {BOARD_COLORS.map((c) => (
            <button key={c} type="button" className="dr-swatch" role="radio" aria-checked={color === c}
              data-color={c} onClick={() => setColor(c)} aria-label={COLOR_NAMES[c]} title={COLOR_NAMES[c]} />
          ))}
        </div>
        <span className="dr-sep" aria-hidden="true" />
        <div className="dr-group">
          <button type="button" className="dr-tool dr-undo" onClick={() => engine.undo('board')}
            aria-label="Undo my last stroke" title="Undo my last stroke (Ctrl+Z)">{ICONS.undo}</button>
          <div className="dr-pop-wrap">
            <button type="button" className="dr-tool dr-clear" aria-expanded={clearing !== null}
              onClick={() => setClearing(clearing ? null : 'menu')} aria-label="Clear" title="Clear">
              {ICONS.clear}
            </button>
            {clearing && (
              <div className="dr-pop" role="dialog" aria-label="Clear the whiteboard">
                {clearing === 'menu' ? (
                  <>
                    <button type="button" className="dr-pop-item dr-clear-mine"
                      onClick={() => { engine.clear('board', 'mine'); setClearing(null); }}>
                      Clear mine
                    </button>
                    <button type="button" className="dr-pop-item dr-clear-all"
                      onClick={() => setClearing('confirm')}>
                      Clear everyone's…
                    </button>
                  </>
                ) : (
                  <>
                    <p className="dr-pop-q">Clear the whole board for everybody in the call?</p>
                    <div className="dr-pop-row">
                      <button type="button" className="dr-pop-danger dr-clear-all-yes"
                        onClick={() => { engine.clear('board', 'all'); setClearing(null); }}>
                        Clear all
                      </button>
                      <button type="button" className="dr-pop-item" onClick={() => setClearing(null)}>Cancel</button>
                    </div>
                  </>
                )}
              </div>
            )}
          </div>
          <button type="button" className="dr-tool dr-export" onClick={exportPng}
            aria-label="Save the board as a picture" title="Save as PNG">{ICONS.save}</button>
          {saved && <span className="dr-saved" role="status">Saved</span>}
        </div>
      </div>

      <div className="dr-area" ref={areaRef} data-tool={tool}
        onPointerDown={down} onPointerMove={move} onPointerUp={up} onPointerCancel={up}
        onPointerLeave={() => setRing(null)}>
        <canvas ref={canvasRef} className="dr-canvas dr-board-canvas" aria-label="Whiteboard drawing area" role="img" />
        {tool === 'eraser' && ring && (
          <span className="dr-ring" aria-hidden="true"
            style={{ left: ring.x, top: ring.y, width: box().h * 0.044, height: box().h * 0.044 }} />
        )}
        {textAt && (
          <input ref={textRef} className="dr-text-input" type="text" maxLength={MAX_TEXT}
            value={textValue} placeholder="Type, then Enter"
            aria-label="Text to place on the board"
            style={{ left: textAt.x, top: textAt.y }}
            onChange={(e) => setTextValue(e.target.value)}
            onPointerDown={(e) => e.stopPropagation()}
            onKeyDown={(e) => {
              if (e.key === 'Enter') { e.preventDefault(); commitText(); }
              if (e.key === 'Escape') { setTextAt(null); setTextValue(''); }
            }}
            onBlur={commitText} />
        )}
      </div>
    </section>
  );
};

export default Whiteboard;
